import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { makeStyles } from '@material-ui/core/styles';

const SIZE = 14;

const useStyles = makeStyles(theme => ({
  '@keyframes hotspotPulse': {
    '0%': { transform: 'scale(1)', opacity: 0.7 },
    '100%': { transform: 'scale(3)', opacity: 0 },
  },
  dot: {
    position: 'fixed',
    width: SIZE,
    height: SIZE,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: theme.shadows[3],
    // Clicks go through to the element underneath.
    pointerEvents: 'none',
    // Above the tour overlay (zIndex.modal + 1) and the drawers it explains.
    zIndex: theme.zIndex.modal + 2,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: -2,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      animation: '$hotspotPulse 1.4s ease-out infinite',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&::after': { animation: 'none' },
    },
  },
}));

/**
 * Pulsing dot on the element a tour step asks the user to click or fill. Follows the element
 * while the page scrolls or the drawer animates.
 */
export function Hotspot({ element }: { element: HTMLElement }) {
  const classes = useStyles();
  const [position, setPosition] = useState<{ top: number; left: number }>();

  useEffect(() => {
    let frame = 0;
    const follow = () => {
      const rect = element.getBoundingClientRect();
      // On the top right corner, like a notification badge: it never hides the label.
      const next = {
        top: Math.round(rect.top - SIZE / 2 + 2),
        left: Math.round(rect.right - SIZE / 2 - 2),
      };
      setPosition(prev =>
        prev?.top === next.top && prev?.left === next.left ? prev : next,
      );
      frame = requestAnimationFrame(follow);
    };
    follow();
    return () => cancelAnimationFrame(frame);
  }, [element]);

  if (!position) return null;
  return createPortal(
    <span className={classes.dot} style={position} aria-hidden />,
    document.body,
  );
}
